<template>
  <div>
    <section class="d-flex">
      <BreadCrumb :items="$route.matched"></BreadCrumb>
    </section>

    <div v-if="!loading">
      <div class="ctrl-btns">
        <v-btn @click="$router.back()" depressed>
          <v-icon left>fas fa-angle-left</v-icon>
          戻る
        </v-btn>
      </div>

      <h3>
        概要
        <div class="ctrl-btn opened" @click="switchAccordion"></div>
      </h3>
      <section class="accordion_inner">
        <v-form v-model="valid">
          <v-row>
            <v-col cols="6" md="3">
              <v-text-field
                ref="code"
                label="案件コード"
                v-model="editedItem.code"
                :disabled="!editable"
                outlined hide-details="auto">
              </v-text-field>
            </v-col>
            <v-col cols="6" md="9">
              <v-text-field
                ref="name"
                label="案件名"
                v-model="editedItem.name"
                :rules="[rules.required]"
                class="required"
                :disabled="!editable"
                outlined hide-details="auto">
              </v-text-field>
            </v-col>
            <v-col cols="6" md="3">
              <v-combobox
                ref="corporateID"
                label="担当企業"
                v-model="editedItem.corporateID"
                :rules="[rules.required]"
                class="required"
                :items="corporateLabels"
                :disabled="!editable"
                append-icon="fas fa-list-ol"
                @click.stop="openSelected('corporateID')"
                readonly outlined hide-details="auto">
              </v-combobox>
            </v-col>
            <v-col cols="6" md="3">
              <v-combobox
                ref="customerID"
                label="客先"
                v-model="editedItem.customerID"
                :rules="[rules.required]"
                class="required"
                :items="customers"
                :disabled="!editable"
                append-icon="fas fa-list-ol"
                @click.stop="openSelected('customerID')"
                readonly outlined hide-details="auto">
              </v-combobox>
            </v-col>
            <v-col cols="6" md="3">
              <v-menu
                v-model="delivery"
                :close-on-content-click="false"
                :nudge-right="0"
                transition="scale-transition" offset-y min-width="290px">
                <template v-slot:activator="{ on }">
                  <v-text-field
                    ref="deliveryDate"
                    label="希望納期"
                    v-model="editedItem.deliveryDate"
                    v-on="on"
                    :disabled="!editable"
                    outlined clearable hide-details="auto">
                  </v-text-field>
                </template>
                <v-date-picker v-model="editedItem.deliveryDate" @input="delivery = false"></v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="12">
              <v-textarea
                ref="description"
                label="備考"
                v-model="editedItem.description"
                rows="10"
                :disabled="!editable"
                outlined hide-details="auto"></v-textarea>
            </v-col>
          </v-row>
        </v-form>
        <div class="d-flex mt-3">
          <v-spacer></v-spacer>
          <v-btn 
            color="primary" 
            class="ml-2" 
            v-if="editable"
            @click.stop="save" 
            :loading="saving" 
            :disabled="!valid" depressed>
            <v-icon left>fas fa-save</v-icon>更新
          </v-btn>
          <v-btn 
            color="error" 
            class="ml-2" 
            v-if="editable"
            @click.stop="remove" 
            :loading="saving" depressed>
            <v-icon left>fas fa-trash-alt</v-icon>削除
          </v-btn>
          <v-btn 
            color="default" 
            class="ml-2" 
            v-if="editable"
            @click.stop="editable = false" depressed>
            <v-icon left>fas fa-times</v-icon>キャンセル
          </v-btn>
          <v-btn 
            color="default" 
            class="ml-2" 
            v-if="!editable"
            @click.stop="editable = true" depressed>
            <v-icon left>fas fa-pen</v-icon>編集
          </v-btn>
        </div>
      </section>

      <template v-if="isAllowed('見積・請求')">
        <h3>
          伝票
          <div class="ctrl-btn opened" @click="switchAccordion"></div>
        </h3>
        <section class="accordion_inner">
          <div class="d-flex my-3">
            <v-spacer></v-spacer>
            <v-btn 
              color="primary" 
              class="ml-2" 
              @click.stop="slipOpen" depressed>
              <v-icon left>fas fa-pen</v-icon>伝票を作成
            </v-btn>
          </div>
          <!-- 伝票 -->
          <v-data-table
            :headers="orderHeaders"
            :items="orders"
            :sort-by="['estimateDate']"
            :sort-desc="[true]"
            :loading="loading"
            @click:row="slipOpen">
              <template v-slot:item.corporateID="{ item }">
                <template v-if="getCorporate(item.corporateID)">
                  {{ getCorporate(item.corporateID) }}
                </template>
              </template>
              <template v-slot:item.amount="{ item }">
                &yen; {{ separate(Math.floor(item.amount * (item.tax / 100 + 1) )) }}
              </template>
              <template v-slot:item.complete="{ item }">
                <template v-if="item.complete">
                  <v-icon color="green darken-2">far fa-check-circle</v-icon>
                </template>
              </template>
              <template v-slot:item.estimateDate="{ item }">
                {{ getDateTime(item.estimateDate, 'YYYY-MM-DD') }}
              </template>
              <template v-slot:item.claimDate="{ item }">
                {{ getDateTime(item.claimDate, 'YYYY-MM-DD') }}
              </template>
          </v-data-table>
        </section>
      </template>

      <h3>
        工程
        <div class="ctrl-btn opened" @click="switchAccordion"></div>
      </h3>
      <section class="accordion_inner">
        <div class="d-flex my-3">
          <v-spacer></v-spacer>
          <v-btn 
            color="primary" 
            class="ml-2" 
            @click.stop="eventDialog = !eventDialog" depressed>
            <v-icon left>fas fa-pen</v-icon>工程を作成
          </v-btn>
        </div>
        <!-- 工程表 -->
        <ScheduleTimeline
          :tasks="resources"
          @taskClick="taskClick">
        </ScheduleTimeline>
      </section>

      <h3>
        資料
        <div class="ctrl-btn opened" @click="switchAccordion"></div>
      </h3>
      <section class="accordion_inner">
        <div class="py-2">
          <ul class="list-inline gallery">
            <li v-for="file in documents" :key="file.id">
              <Upload
                :file.sync="file.file"
                :label.sync="file.name"
                :labelEditable="true"
                :deletable="true"
                @lightbox="lightboxFunc"
                @uploadFile="uploadFile(file)"
                @deleteFile="deleteFile(file)"
                accept="image/*,.pdf" />
            </li>
            <li>
              <v-btn tile icon outlined class="add-btn" @click.stop="addFile">
                <v-icon>fas fa-plus</v-icon>
              </v-btn>
            </li>
          </ul>
        </div>
      </section>

      <!-- 伝票ダイアログ -->
      <Slip
        :editedItem="slipItem"
        :customers="customers"
        :users="users"
        :corporates="corporates"
        :payments="customer.payment"
        :projectID="editedItem.id"
        :show="slipDialog"
        @close="slipDialog = false" />

      <!-- 工程ダイアログ -->
      <v-navigation-drawer v-model="eventDialog" fixed temporary right stateless width="600" id="event-dialog">
        <v-card flat>
          <v-card-title>
            <div class="title">工程の作成</div>
          </v-card-title>
          <v-card-text>
            <v-form v-model="eventValid">
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    ref="name"
                    label="項目"
                    v-model="eventItem.name"
                    :rules="[rules.required]"
                    class="required"
                    outlined hide-details="auto">
                  </v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-combobox
                    ref="parentID"
                    label="親工程"
                    :items="anotherProcess"
                    v-model="eventItem.parentID"
                    clearable outlined hide-details="auto">
                  </v-combobox>
                </v-col>
                <v-col cols="12">
                  <v-combobox
                    ref="dependentOn"
                    label="完了が必要な工程"
                    :items="anotherProcess"
                    v-model="eventItem.dependentOn"
                    multiple clearable outlined hide-details="auto">
                  </v-combobox>
                </v-col>
                <v-col cols="12">
                  <v-combobox
                    ref="charge"
                    label="担当"
                    :items="users"
                    v-model="eventItem.charge"
                    multiple clearable outlined hide-details="auto">
                  </v-combobox>
                </v-col>
                <v-col cols="6">
                  <v-menu 
                    v-model="start" 
                    :close-on-content-click="false" 
                    :nudge-right="0" 
                    transition="scale-transition" 
                    offset-y min-width="290px">
                    <template v-slot:activator="{ on }">
                      <v-text-field 
                        ref="start" 
                        label="開始日"
                        v-model="eventItem.start" 
                        :rules="[rules.required]" 
                        class="required"
                        v-on="on" 
                        readonly outlined hide-details="auto">
                      </v-text-field>
                    </template>
                    <v-date-picker v-model="eventItem.start" @input="start = false" :max="eventItem.end"></v-date-picker>
                  </v-menu>
                </v-col>
                <v-col cols="6">
                  <v-menu 
                    v-model="end" 
                    :close-on-content-click="false" 
                    :nudge-right="0" 
                    transition="scale-transition" 
                    offset-y min-width="290px">
                    <template v-slot:activator="{ on }">
                      <v-text-field 
                        ref="end" 
                        label="終了日"
                        v-model="eventItem.end" 
                        :rules="[rules.required]" 
                        class="required"
                        v-on="on" 
                        readonly outlined hide-details="auto">
                      </v-text-field>
                    </template>
                    <v-date-picker v-model="eventItem.end" @input="end = false" :min="eventItem.start"></v-date-picker>
                  </v-menu>
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    ref="progress"
                    label="進捗率"
                    type="number"
                    v-model="eventItem.progress"
                    :rules="[rules.required, rules.integer, rules.min0, rules.max100]"
                    class="required"
                    append-icon="fas fa-percentage"
                    outlined hide-details="auto">
                  </v-text-field>
                </v-col>
                <v-col cols="6">
                  <v-swatches v-model="eventItem.color">
                    <v-text-field
                      ref="color"
                      label="色"
                      slot="trigger"
                      :background-color="eventItem.color"
                      v-model="eventItem.color"
                      readonly outlined hide-details="auto">
                    </v-text-field>
                  </v-swatches>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-btn color="primary" @click="saveEvent" :loading="saving" :disabled="!eventValid" text>
              <v-icon left>fas fa-save</v-icon>保存
            </v-btn>
            <v-btn color="error" @click="removeEvent" :loading="saving" v-if="eventIndex > -1" text>
              <v-icon left>fas fa-trash-alt</v-icon>削除
            </v-btn>
            <v-btn @click="eventDialog = false" text>
              <v-icon left>fas fa-times</v-icon>キャンセル
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-navigation-drawer>
    </div>
    <Loading :visible="loading"></Loading>

  </div>
</template>

<script>
import { API, graphqlOperation, Storage } from 'aws-amplify'
import { 
  updateProject, deleteProject, 
  createDocument, updateDocument, deleteDocument, 
  createProcess, updateProcess, deleteProcess } from '../graphql/mutations'
import { 
  getProject, listDocuments, listOrders, 
  listProcesses, listCustomers, listUsers, listCorporates } from '../graphql/queries'
import { 
  onCreateOrder, onUpdateOrder, onDeleteOrder,
  onCreateProcess, onUpdateProcess, onDeleteProcess } from '../graphql/subscriptions'
import ScheduleTimeline from '@/components/ScheduleTimeline'
import Upload from '@/components/Upload'
import Slip from '@/components/Slip'
import moment from 'moment'
import VSwatches from 'vue-swatches'
import 'vue-swatches/dist/vue-swatches.css'

export default {
  data() {
    return {
      search: '',
      loading: true,
      dialog: false,
      valid: false,
      saving: false,
      editable: false,
      customers: [],
      users: [],
      corporates: [],
      editedIndex: -1,
      editedItem: {},
      documents: [],
      orders: [],
      orderHeaders: [
        { text: '伝票No', align: 'start', value: 'id' },
        { text: '件名', align: 'start', value: 'title' },
        { text: '発行元', align: 'start', value: 'corporateID' },
        { text: '見積日', align: 'start', value: 'estimateDate' },
        { text: '請求日', align: 'start', value: 'claimDate' },
        { text: '支払完了', align: 'start', value: 'complete' },
        { text: '合計金額（税込）', align: 'end', value: 'amount' },
      ],
      tasks: [],
      filterKeyword: {},
      subscription: {},
      customer: {},
      logoURL: null,
      delivery: false,
      groups: [],
      slipDialog: false,
      slipItem: {},
      eventDialog: false,
      eventValid: false,
      eventIndex: -1,
      eventItem: {
        color: '#e74c3c',
      },
      start: false,
      end: false,
    }
  },
  prop: {},
  mounted: async function() {
    let timer = setInterval(() => {
      if (this.getMyself()) {
        clearInterval(timer)
        this.init();
      }
    }, 100)

    /*---------------------------
    * 伝票
    ---------------------------*/
    //作成時
    await API.graphql(graphqlOperation(onCreateOrder)).subscribe({
      next: (e) => {
        const row = e.value.data.onCreateOrder
        this.orders.push(row)
      }
    })
    //更新時
    await API.graphql(graphqlOperation(onUpdateOrder)).subscribe({
      next: (e) => {
        const row = e.value.data.onUpdateOrder
        this.orders.find(origin => {
          if (origin.id === row.id) {
            origin = Object.assign(origin, row)
          }
        })
      }
    })
    //削除時 
    await API.graphql(graphqlOperation(onDeleteOrder)).subscribe({
      next: (e) => {
        const row = e.value.data.onDeleteOrder
        this.orders = this.orders.filter(origin => origin.id !== row.id)
      }
    })
    /*---------------------------
    * 工程
    ---------------------------*/
    //作成時
    await API.graphql(graphqlOperation(onCreateProcess)).subscribe({
      next: (e) => {
        const row = e.value.data.onCreateProcess
        this.tasks.push(row)
      }
    })
    //更新時
    await API.graphql(graphqlOperation(onUpdateProcess)).subscribe({
      next: (e) => {
        const row = e.value.data.onUpdateProcess
        this.tasks.find(origin => {
          if (origin.id === row.id) {
            origin = Object.assign(origin, row)
          }
        })
      }
    })
    //削除時 
    await API.graphql(graphqlOperation(onDeleteProcess)).subscribe({
      next: (e) => {
        const row = e.value.data.onDeleteProcess
        this.tasks = this.tasks.filter(origin => origin.id !== row.id)
      }
    })
  },
  beforeDestroy() {},
  created() {
    //if (!this.isAllowed('プロジェクト')) this.$router.push('/')
    
  },
  computed: {
    project() {
      return {
        id: this.editedItem.id || null,
        code: this.editedItem.code || null,
        name: this.editedItem.name || null,
        corporateID: this.editedItem.corporateID ? this.editedItem.corporateID.value: null,
        customerID: this.editedItem.customerID ? this.editedItem.customerID.value: null,
        deliveryDate: this.editedItem.deliveryDate || null,
      }
    },
    task() {
      return {
        id: this.eventItem.id || null,
        projectID: this.editedItem.id,
        name: this.eventItem.name,
        parentID: this.eventItem.parentID ? this.eventItem.parentID.value : null,
        dependentOn: this.eventItem.dependentOn ? this.eventItem.dependentOn.map(e => e.value) : [],
        charge: this.eventItem.charge ? this.eventItem.charge.map(e => e.value || e) : [],
        start: this.eventItem.start || null,
        end: this.eventItem.end || null,
        color: this.eventItem.color || null,
        progress: this.eventItem.progress || 0,
      }
    },
    resources() {
      const map = []
      for (const item of this.tasks) {
        //担当
        const charge = []
        for (const id of item.charge) {
          const user = this.users.find(e => e.value === id)
          charge.push(user.text || id)
        }

        map.push(Object.assign({
          chargeText: charge.join(', ')
        }, item))
      }
      return map
    },
    anotherProcess() {
      const map = []
      for (const item of this.tasks.filter(e => e.id != this.eventItem.id)) {
        map.push({
          value: item.id,
          text: item.name,
        })
      }
      return map
    },
    eventForm() {
      const map = [
        'name',
        'parentID',
        'charge',
        'start',
        'end',
        'progress'
      ]
      return map
    },
    corporateLabels() {
      const map = []

      for (const item of this.corporates) {
        map.push({
          value: item.id,
          text: item.name,
        })
      }

      return map
    },
  },
  watch: {
    eventDialog(val) {
      val || this.eventClose()
    },
  },
  methods: {
    //初期化
    async init() {
      try {
        const id = this.$route.params.id
        //客先 query
        await this.getCustomers()
        //ユーザー query
        await this.getUsers()
        //企業情報 query
        await this.getCorporates()
        //プロジェクト詳細
        const query = await API.graphql(graphqlOperation(getProject, { id: id }))
        const data = query.data.getProject
        const description = await Storage.get('project/description/' + data.id + '.html', { download: true })
        this.editedItem = {
          id: data.id,
          code: data.code || null,
          name: data.name,
          corporateID: this.corporateLabels.find(e => e.value == data.corporateID),
          customerID: this.customers.find(e => e.value == data.customerID),
          deliveryDate: data.deliveryDate || null,
          description: await this.readBlogToTextAsync(description.Body),
        }
        this.customer = Object.assign({}, data.customer)
        //資料
        await this.getDocuments(this.documents, data.id)
        //伝票
        await this.getOrders(data.id)
        //工程
        await this.getProcesses(data.id)

        this.loading = false

      } catch (e) {
        //this.$router.push('/')
        //console.log(e)
      }

    },
    //保存
    async save() {
      try {
        this.saving = true
        const row = this.project
        let id = row.id
        //更新
        await API.graphql(graphqlOperation(updateProject, {input: row}))
        this.log({ type: this.$route.name, text: '[' + row.name + '] を更新しました' })
        //説明
        await Storage.put('project/description/' + id + '.html', this.editedItem.description)

        this.editable = false
      } catch (e) {
        //console.log(e)
        this.myAlert(e)
        this.saving = false
      }
    },
    //削除
    async remove() {
      try {
        if (confirm(this.editedItem.name + 'を削除しますか？')) {
          //プロジェクト削除
          await API.graphql(graphqlOperation(deleteProject, {
            input: { id: this.editedItem.id }
          }))
          this.log({ type: this.$route.name, text: '[' + this.editedItem.name + '] を削除しました' })
          this.myAlert(this.editedItem.name + 'を削除しました', 'success')

          this.$router.push('/projects')
        }
      } catch (e) {
        //console.log(e)
        this.myAlert(e)
      }
    },
    //アコーディオンの開閉
    switchAccordion(e) {
      const target = e.target.parentElement.nextSibling
      target.style.display = target.style.display == 'none' ? 'block' : 'none'
      e.target.classList.toggle('opened')
    },
    //法人名
    getCorporate(id) {
      const corporate = this.corporateLabels.find(e => e.value === id)

      return corporate ? corporate.text : null
    },
    //ファイルの取得
    async getDocuments(target, projectID, nextToken = null) {
      const query = await API.graphql(graphqlOperation(listDocuments, {
        filter: {
          projectID: { eq: projectID }
        },
        limit: 100,
        nextToken: nextToken
      }))
      for (const data of query.data.listDocuments.items) {
        target.push(data)
      }
      if (query.data.listDocuments.nextToken) this.getDocuments(target, projectID, query.data.listDocuments.nextToken)
    },
    //受注リストの取得
    async getOrders(projectID, nextToken = null) {
      //所属企業
      const myself = this.getMyself()
      const corporateFilter = []
      for (const id of myself.corporateIDs) {
        corporateFilter.push({
          corporateID: { eq: id }
        })
      }
      const query = await API.graphql(graphqlOperation(listOrders, { 
        filter: {
          projectID: { eq: projectID },
          or: corporateFilter
        },
        limit: 500,
        nextToken: nextToken
      }))
      for (const row of query.data.listOrders.items) {
        this.orders.push(row)
      }
      if (query.data.listOrders.nextToken) this.getOrders(projectID, query.data.listOrders.nextToken)
    },
    //工程の取得
    async getProcesses(projectID, nextToken = null) {
      const query = await API.graphql(graphqlOperation(listProcesses, {
        filter: {
          projectID: { eq: projectID }
        },
        limit: 500,
        nextToken: nextToken
      }))
      for (const data of query.data.listProcesses.items) {
        this.tasks.push(data)
      }
      //開始時間でソート
      this.tasks.sort(function(a,b) {
        if (a.start < b.start) return -1
        if (a.start > b.start) return 1
        return 0
      })
      if (query.data.listProcesses.nextToken) this.getProcesses(projectID, query.data.listProcesses.nextToken)
    },
    //客先の取得
    async getCustomers(nextToken = null) {
      const query = await API.graphql(graphqlOperation(listCustomers, { limit: 500, nextToken: nextToken }))
      for (const data of query.data.listCustomers.items) {
        this.customers.push({
          value: data.id,
          text: data.name + (data.office ? '（' + data.office + '）' : ''),
        })
      }
      if (query.data.listCustomers.nextToken) this.getCustomers(query.data.listCustomers.nextToken)
    },
    //ユーザーの取得
    async getUsers(nextToken = null) {
      const query = await API.graphql(graphqlOperation(listUsers, { limit: 500, nextToken: nextToken }))
      for (const data of query.data.listUsers.items) {
        this.users.push({
          value: data.id,
          text: data.last + ' ' + data.first,
          corporateIDs: data.corporateIDs,
        })
      }
      if (query.data.listUsers.nextToken) this.getUsers(query.data.listUsers.nextToken)
    },
    //企業の取得
    async getCorporates(nextToken = null) {
      //所属企業
      const myself = this.getMyself()
      const corporateFilter = []
      for (const id of myself.corporateIDs) {
        corporateFilter.push({
          id: { eq: id }
        })
      }
      const query = await API.graphql(graphqlOperation(listCorporates, { 
        filter: {
          or: corporateFilter
        },
        limit: 500, 
        nextToken: nextToken 
      }))
      for (const data of query.data.listCorporates.items) {
        this.corporates.push(data)
      }
      if (query.data.listCorporates.nextToken) this.getCorporates(query.data.listCorporates.nextToken)
    },
    //ファイルの追加
    addFile() {
      this.documents.push({
        file: null,
        name: '名称未設定',
      })
    },
    //資料アップロード
    async uploadFile(item) {
      delete item.createdAt
      delete item.project
      delete item.updatedAt

      if (item.id) {
        await API.graphql(graphqlOperation(updateDocument, {input: item}))
      } else {
        item.projectID = this.editedItem.id
        const mutation = await API.graphql(graphqlOperation(createDocument, {input: item}))
        item.id = mutation.data.createDocument.id
      }
    },
    //資料削除
    async deleteFile(item) {
      if (window.confirm('資料を削除しますか？')) {
        if (item.id) {
          this.documents = this.documents.filter(e => e.id != item.id)
          await API.graphql(graphqlOperation(deleteDocument, {
            input: { id: item.id }
          }))
        } else {
          this.documents = this.documents.filter(e => e != item)
        }
      }
    },
    //伝票Open
    slipOpen(param) {
      //項目の補足
      this.slipItem = Object.assign({
        id: null,
        title: null,
        corporateID: null,
        customer: null,
        customerID: null,
        agencyID: null,
        amount: null,
        items: [],
        estimateDate: moment().format('YYYY-MM-DD'),
        claimDate: null,
        deliveryDate: null,
        fixedDate: null,
        orderDate: null,
        paymentDate: null,
        transactionDate: null,
        paymentTerms: null,
        processID: null,
        processPass: null,
        estimateLimit: '見積日より1ヶ月',
        projectID: null,
        transactionID: null,
        remarks: null,
        status: null,
        tax: 10,
        payment: this.customer.payment[0],
        complete: false,
      }, param)

      // 客先がPJのものと変更されている場合は、変更先の客先にする
      if (param.customerID) {
        const customer = this.customers.find(e => e.value === param.customerID)
        this.slipItem.customerID = customer
      } else {
        this.slipItem.customerID = this.editedItem.customerID
      }

      this.slipDialog = true
    },
    //工程ダイアログclose
    eventClose() {
      this.saving = false
      setTimeout(() => {
        this.eventResetForm()
        this.eventIndex = -1
        //スクロールリセット
        let container = document.getElementById('event-dialog').getElementsByClassName('v-navigation-drawer__content')[0]
        container.scrollTop = 0
      }, 300)
    },
    //フォームreset
    eventResetForm() {
      for (const name of this.eventForm) {
        if (this.$refs[name] && !!this.$refs[name].reset) this.$refs[name].reset()
      }
      this.eventItem = {
        color: '#e74c3c',
      }
    },
    //工程選択
    taskClick(item) {
      const org = Object.assign({}, item)

      this.eventIndex = this.tasks.findIndex(e => e.id === org.id)
      //親工程
      if (org.parentID) {
        const parent = this.anotherProcess.find(e => e.value === org.parentID)
        org.parentID = parent
      }
      //完了が必要な工程
      const depend = []
      if (org.dependentOn) {
        for (const dependID of org.dependentOn) {
          const task = this.anotherProcess.find(e => e.value === dependID)
          depend.push(task || dependID)
        }
      }
      org.dependentOn = depend
      //担当
      const map = []
      for (const charge of org.charge) {
        const user = this.users.find(e => e.value === charge)
        map.push(user || charge)
      }
      org.charge = map
      
      this.eventItem = org
      this.eventDialog = true
    },
    //工程の保存
    async saveEvent() {
      try {
        this.saving = true
        const data = Object.assign({}, this.task)
        //編集
        if (this.eventIndex >= 0) {
          await API.graphql(graphqlOperation(updateProcess, {input: data}))
          this.log({ type: this.$route.name, text: '[' + data.name + '] を更新しました' })
        //新規
        } else {
          delete data.id
          await API.graphql(graphqlOperation(createProcess, {input: data}))
          this.log({ type: this.$route.name, text: '[' + data.name + '] を追加しました' })
        }
        this.eventDialog = false
      } catch (error) {
        this.myAlert(error.message)
        this.saving = false
      }
    },
    //工程の削除
    async removeEvent() {
      try {
        if (confirm(this.eventItem.name + 'を削除しますか？')) {
          //削除
          await API.graphql(graphqlOperation(deleteProcess, {
            input: { id: this.eventItem.id }
          }))
          this.log({ type: this.$route.name, text: '[' + this.eventItem.name + '] を削除しました' })
          this.myAlert(this.eventItem.name + 'を削除しました', 'success')
          this.eventDialog = false
        }
      } catch (error) {
        this.myAlert(error)
      }
    },
    //S3バケットのURLを返す
    async getLogoURL(key) {
      try {
        const ret = await Storage.get(key)
        this.logoURL = ret
      } catch(e) {
        console.log(e)
      }
    },
  },
  components: {
    Upload,
    ScheduleTimeline,
    VSwatches,
    Slip,
  },
}
</script>

<style lang='scss' scoped>
h3 {
  position: relative;
  .ctrl-btn {
    position: absolute;
    right: 10px;
    top: 10px;
    cursor: pointer;
    font-size: 20px;
    &:after {
      content: "\f139";
      font-family: 'Font Awesome 5 Free';
      font-weight: 900;
    }
    &.opened:after {
      content: "\f13a";
    }
  }
}
.gallery {
  li {
    margin-right: 10px;
    vertical-align: top;
    .add-btn {
      border-color: #DDD;
      border-radius: 5px;
      width: 120px;
      height: 120px;
    }
  }
}
.ctrl-btns {
  margin-bottom: 20px;
}
</style>
